import { engineComponent } from "neo-common";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { ComponentTypes } from "../Components/ComponentTypes";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";

@engineComponent(ComponentTypes.DisplayObject)
export class MinigameElement extends ContainerElement {
    private _htmlElement: HTMLElement;
    private _data: Array<number> = [];

    private board: BaseElement;
    private boardElements: Array<SwitchElement> = [];
    private boardCounters: Array<TimelineElement> = [];
    private boardButtons: Array<HTMLElement> = [];

    private introElements: Array<TimelineElement> = [];
    private outroElements: Array<TimelineElement> = [];

    private activeElements: Array<SwitchElement> = [];
    private activeElementsCase: string;

    static CaseNowin: string = "NORMAL";
    static CaseWin: string = "WIN";
    static CaseNowinSilent: string = "NORMAL";
    static CaseWinSilent: string = "WIN_SILENT";
    static CaseOn: string = "ON";
    static CaseOff: string = "OFF";

    public static readonly EventButtonAction: string = "buttonAction";
    public static readonly EventIntroFinished: string = "introFinished";
    public static readonly EventOutroFinished: string = "outroFinished";

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.style) {
            // update attributes from style
            for (let attribute in MinigameElementAttributes) {
                const name = MinigameElementAttributes[attribute];
                if (this.style.node.hasAttribute(name)) {
                    node.setAttribute(name, this.style.node.getAttribute(name));
                }
            }
        }

        if (node.hasAttribute(MinigameElementAttributes.ACTIVE_ELEMENTS)) {
            let names = node.getAttribute(MinigameElementAttributes.ACTIVE_ELEMENTS).split(";");
            names.forEach((name) => {
                if (this.childs[name]) {
                    this.activeElements.push(this.childs[name]);
                }
            });
        }

        if (node.hasAttribute(MinigameElementAttributes.BOARD_CONTAINER)) {
            let el = this.childs[node.getAttribute(MinigameElementAttributes.BOARD_CONTAINER)];
            if (el && el instanceof BaseElement) {
                this.board = el;
            } else {
                this.board = new ContainerElement();
                this.addChild(this.board);
            }
        } else {
            this.board = new ContainerElement();
            this.addChild(this.board);
        }

        if (node.hasAttribute(MinigameElementAttributes.BOARD_ELEMENT_NAMES)) {
            let boardElementNames = node.getAttribute(MinigameElementAttributes.BOARD_ELEMENT_NAMES).split(";");
            this.boardElements = [];
            boardElementNames.forEach((name, index) => {
                if (this.childs[name] && this.childs[name] instanceof SwitchElement) {
                    this.boardElements[index] = this.childs[name];
                    this.board.addChild(this.childs[name]);
                }
            });
        }

        if (node.hasAttribute(MinigameElementAttributes.BOARD_BUTTON_NAMES)) {
            let boardButtonNames = node.getAttribute(MinigameElementAttributes.BOARD_BUTTON_NAMES).split(";");
            this.boardButtons = [];
            boardButtonNames.forEach((name, index) => {
                let el = document.getElementById(name);
                if (el) {
                    this.boardButtons[index] = el;
                    el.addEventListener("click", this.buttonAction.bind(this, el, index));
                }
            });
        }

        if (node.hasAttribute(MinigameElementAttributes.BOARD_COUNTER_NAMES)) {
            let boardCounterNames = node.getAttribute(MinigameElementAttributes.BOARD_COUNTER_NAMES).split(";");
            this.boardCounters = [];
            boardCounterNames.forEach((name, index) => {
                if (this.childs[name] && this.childs[name] instanceof TimelineElement) {
                    this.boardCounters[index] = this.childs[name];
                    this.board.addChild(this.childs[name]);
                }
            });
        }

        if (node.hasAttribute(MinigameElementAttributes.INTRO_ELEMENT_NAME)) {
            let names = node.getAttribute(MinigameElementAttributes.INTRO_ELEMENT_NAME).split(";");
            names.forEach((name) => {
                const element = this.childs[name];
                if (element instanceof TimelineElement) {
                    this.introElements.push(element);
                    element.on("board", this.showBoard.bind(this));
                    element.on(
                        "finished",
                        function () {
                            this.emit(MinigameElement.EventIntroFinished);
                        }.bind(this),
                    );
                    element.visible = false;
                }
            });
        }

        if (node.hasAttribute(MinigameElementAttributes.OUTRO_ELEMENT_NAME)) {
            let names = node.getAttribute(MinigameElementAttributes.OUTRO_ELEMENT_NAME).split(";");
            names.forEach((name) => {
                const element = this.childs[name];
                if (element instanceof TimelineElement) {
                    this.outroElements.push(element);
                    element.on("board", this.hideBoard.bind(this));
                    element.on(
                        "finished",
                        function () {
                            this.visible = false;
                            this.emit(MinigameElement.EventOutroFinished);
                        }.bind(this),
                    );
                    element.visible = false;
                }
            });
        }

        if (node.hasAttribute(MinigameElementAttributes.HTML_ELEMENT_NAME)) {
            let htmlElement = document.getElementById(node.getAttribute(MinigameElementAttributes.HTML_ELEMENT_NAME));
            if (htmlElement) {
                this._htmlElement = htmlElement;
            }
        }
    }

    updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.getAttribute(MinigameElementAttributes.DATA_RESET) == "true") {
            this._data = null;
        }

        if (node.hasAttribute(MinigameElementAttributes.DATA)) {
            this.setData(
                node
                    .getAttribute(MinigameElementAttributes.DATA)
                    .split(";")
                    .map((value) => {
                        let parsed: number = parseFloat(value);
                        return isNaN(parsed) ? null : parsed;
                    }),
            );
        }
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        super.internalDestroy(options);
        this.boardElements = null;
        this.boardCounters = null;
        this.boardButtons.forEach((el, index) => {
            el.removeEventListener(this.buttonAction.bind(this, el, index));
        });
        this.boardButtons = null;
    }

    private buttonAction(element: HTMLElement, index: number): void {
        element.classList.add("_disabled");
        this.disable();
        this.emit(MinigameElement.EventButtonAction, index);
    }

    public setData(data: Array<number>, silent?: boolean): void {
        let oldData = this._data;
        this._data = data.slice();
        this._data.forEach((value, index) => {
            if (this.boardButtons[index]) {
                if (value !== null) {
                    this.boardButtons[index].classList.add("_disabled");
                } else {
                    this.boardButtons[index].classList.remove("_disabled");
                }
            }
            if (!oldData || (oldData && oldData[index] != value)) {
                if (this.boardCounters && this.boardCounters[index] && value !== null) {
                    this.boardCounters[index].visible = true;
                    if (silent) {
                        this.boardCounters[index].goToOutro(value);
                    } else {
                        this.boardCounters[index].play(value, value);
                    }
                }
                if (this.boardElements && this.boardElements[index]) {
                    if (
                        silent &&
                        this.boardElements[index].hasCase(
                            value !== null ? MinigameElement.CaseWinSilent : MinigameElement.CaseNowinSilent,
                        )
                    ) {
                        if (
                            this.boardElements[index].getCurrentCase() !=
                            (value !== null ? MinigameElement.CaseWinSilent : MinigameElement.CaseNowinSilent)
                        ) {
                            this.boardElements[index].goToCase(
                                value !== null ? MinigameElement.CaseWinSilent : MinigameElement.CaseNowinSilent,
                            );
                        }
                    } else {
                        if (
                            this.boardElements[index].getCurrentCase() !=
                            (value !== null ? MinigameElement.CaseWin : MinigameElement.CaseNowin)
                        ) {
                            this.boardElements[index].goToCase(
                                value !== null ? MinigameElement.CaseWin : MinigameElement.CaseNowin,
                            );
                        }
                    }
                }
            }
        });
        this.enable();
    }

    public intro(data?: Array<number>) {
        if (this._data) {
            this._data = null;
            this.setData(data);
        }
        if (this.introElements.length) {
            this.hideBoard();
            this.introElements.forEach((element) => {
                element.visible = true;
                element.play();
            });
        } else {
            this.showBoard();
        }
        this.visible = true;
        this.setActiveElementCase(MinigameElement.CaseOn);
    }

    public outro(score?: Array<number>) {
        if (this.outroElements.length) {
            this.outroElements.forEach((element, index) => {
                element.visible = true;
                element.play(score && score[index] ? score[index] : null);
            });
        } else {
            this.hideBoard();
        }
        this.setActiveElementCase(MinigameElement.CaseOff);
    }

    public showBoard() {
        this.board.visible = true;
        this.enable();
    }

    public hideBoard() {
        this.board.visible = false;
        this.disable();
    }

    public hideScore() {
        this.boardCounters.forEach((element) => {
            element.visible = false;
        });
    }

    public enable() {
        if (this._htmlElement) {
            this._htmlElement.classList.remove("_disabled");
        }
    }

    public disable() {
        if (this._htmlElement) {
            this._htmlElement.classList.add("_disabled");
        }
    }

    private setActiveElementCase(s: string, emitEvent: boolean = false, delay?: number) {
        this.activeElementsCase = s;
        this.activeElements.forEach((element) => {
            if (element.hasCase(s)) {
                element.goToCase(s, emitEvent, delay);
            }
        });
    }
}

const MinigameElementAttributes = {
    ACTIVE_ELEMENTS: "ActiveElements",

    BOARD_CONTAINER: "BoardContainer",

    BOARD_BUTTON_NAMES: "BoardButtons",
    // names of timeline elements used to show score for board fields
    BOARD_COUNTER_NAMES: "BoardCounters",
    // names of switch elements used as individual board fields
    BOARD_ELEMENT_NAMES: "BoardElements",
    // data for testing
    DATA: "Data",
    DATA_RESET: "DataReset",
    // id of element to click through
    HTML_ELEMENT_NAME: "HTMLElement",
    // intro and outro timeline elements
    INTRO_ELEMENT_NAME: "IntroElement",
    OUTRO_ELEMENT_NAME: "OutroElement",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MinigameElement.ts
 **/